import {
  CancellationCancelReason,
  getCancellationCancelReasonLabel,
} from 'src/app/_enums/cancellation/cancellation-cancel-reason.enum';
import {
  CancellationSendMethod,
  getCancellationSendMethodLabel,
} from 'src/app/_enums/cancellation/cancellation-send-method.enum';
import {
  getInsuranceTypeLabel,
  InsuranceType,
} from 'src/app/_enums/insurance-type.enum';
import { EMPTY_VALUE, fmtDate } from 'src/app/_helpers/utils';
import { CancellationFlowChange } from 'src/app/_types/cancellation.types';

/** The fields a timeline row may report edited, as the reader names them. */
const FIELD_LABELS: Record<string, string> = {
  replacementInsuranceFlow: $localize`Affaire de remplacement`,
  baseInsuranceFlow: $localize`Police à résilier`,
  sendMethod: $localize`Méthode d'envoi`,
  cancellationDate: $localize`Date de résiliation`,
  effectiveCancellationDate: $localize`Date de résiliation effective`,
  customerSupport: $localize`Chargé de clientèle`,
  insuranceType: $localize`Type d'assurance`,
  insurer: $localize`Assureur`,
  policyNumber: $localize`N° de police`,
  comment: $localize`Informations supplémentaires`,
  cancelReason: $localize`Motif d'annulation`,
  document: $localize`Document`,
  displayId: $localize`ID`,
};

/**
 * How a field's stored value is read out. The column stores whatever the
 * backend's diff held — an enum name, an ISO date, a display id — so without
 * this a row would show `REGISTERED_LETTER` where the rest of the page shows
 * « Courrier recommandé ».
 */
const VALUE_LABELS: Record<string, (value: string) => string> = {
  insuranceType: (value) => getInsuranceTypeLabel(value as InsuranceType),
  sendMethod: (value) =>
    getCancellationSendMethodLabel(value as CancellationSendMethod),
  cancellationDate: (value) => fmtDate(value),
  cancelReason: (value) =>
    getCancellationCancelReasonLabel(value as CancellationCancelReason),
};

/** An ISO date, which any field may hold and no reader wants to see raw. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * A missing side of the diff reads as absent whichever way it arrives: the
 * column omits a null rather than serialising it, so `from` and `to` are as
 * often `undefined` as they are `null`.
 */
const readValue = (
  field: string,
  value: string | null | undefined
): string | null => {
  const raw = value?.trim();
  if (!raw) return null;
  const label = VALUE_LABELS[field];
  if (label) return label(raw);
  return ISO_DATE.test(raw) ? fmtDate(raw) : raw;
};

/**
 * One edited field in words: `Date de résiliation : 01/01/2027 → 01/02/2027`.
 *
 * A field that had no value before is stated rather than diffed — « Affaire de
 * remplacement : LTY-2026-0043 », not « – → LTY-2026-0043 » — since the dash
 * carries nothing. One that was cleared keeps the arrow, pointing at the dash,
 * which is the only way to say a value went away without having to agree with
 * the field's gender.
 */
export const describeChange = (change: CancellationFlowChange): string => {
  const field = FIELD_LABELS[change.field] ?? change.field;
  const from = readValue(change.field, change.from);
  const to = readValue(change.field, change.to);

  if (from === null) return `${field} : ${to ?? EMPTY_VALUE}`;
  return `${field} : ${from} → ${to ?? EMPTY_VALUE}`;
};
